import React, { CSSProperties } from 'react';

import BaseTable, { AutoResizer } from 'react-base-table';
import styled, { CSSProp } from 'styled-components';

import { Flex, Text } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import Checkbox from '@adsk/alloy-react-checkbox';
import { AlertWarningIcon, CheckmarkIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { AlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';

import reactBaseTableCSS from './helpers/react-base-table-css';
import { useTranslation } from './i18n';

const TableWrapper = styled.div`
  ${reactBaseTableCSS};
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: hidden;
  & .TableRow:hover {
    cursor: pointer;
    background-color: ${theme.colors.adskBlue100};
  }
  & .TableRow--disabled:hover {
    cursor: not-allowed;
    background-color: ${theme.colors.charcoal100};
  }
`;

const TableFooter = ({
  loading,
  error,
  onRetry,
  loadingMoreText,
  errorText,
}: {
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  loadingMoreText?: React.ReactNode;
  errorText?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  let footer = null;
  if (loading) {
    footer = (
      <>
        <ProgressRing size={ProgressRing.SIZES.SMALL} />
        <Text typography="bodySmall" color="charcoal700" marginLeft="13px">
          {loadingMoreText || t('LOADING_MORE')}
        </Text>
      </>
    );
  } else if (error) {
    footer = (
      <>
        <AlertWarningIcon
          style={{
            color: theme.colors.charcoal700,
          }}
        />
        <Text
          typography="bodySmall"
          color="charcoal700"
          marginLeft="12px"
          marginRight="8px"
        >
          {errorText || t('ERROR_LOADING_ALL_RESULTS')}
        </Text>
        <LinkButton onClick={onRetry} style={theme.typography.bodySmall}>
          {t('TRY_AGAIN')}
        </LinkButton>
      </>
    );
  }

  return (
    <Flex
      alignItems="center"
      paddingLeft="50px"
      typography="bodySmall"
      width="100%"
      height="100%"
    >
      {footer}
    </Flex>
  );
};

type IdType = { id: string };

const SelectionCell = <T extends IdType>({
  rowData,
  onChange,
  disabledIds = [],
  selectedIds = [],
  multiple,
}: {
  rowData: T;
  onChange: ({ entity, checked }: { entity: T; checked?: boolean }) => void;
  disabledIds?: string[];
  selectedIds?: string[];
  multiple?: boolean;
}) => {
  if (!multiple) {
    return selectedIds.includes(rowData.id) ? (
      <CheckmarkIcon style={{ color: theme.colors.adskBlue700 }} />
    ) : null;
  }

  return (
    <Checkbox
      checked={selectedIds.includes(rowData.id)}
      id={rowData.id}
      disabled={disabledIds.includes(rowData.id)}
      onChange={() =>
        onChange({
          entity: rowData,
          checked: !selectedIds.includes(rowData.id),
        })
      }
    />
  );
};

const SelectionCellHeader = <T extends IdType>({
  entities,
  disabledIds = [],
  selectedIds = [],
  setSelection,
}: {
  entities: T[];
  disabledIds?: string[];
  selectedIds?: string[];
  setSelection: (selection: T[]) => void;
}) => {
  const indeterminate =
    entities.some((entity) => selectedIds.includes(entity.id)) &&
    selectedIds.length < entities.length;
  const allChecked = selectedIds.length >= entities.length;
  return (
    <Checkbox
      checked={indeterminate ? Checkbox.INDETERMINATE : allChecked}
      onChange={() =>
        setSelection(
          allChecked
            ? []
            : entities.filter(({ id }) => !disabledIds.includes(id)),
        )
      }
    />
  );
};

type EntitiesTableProps<T extends IdType> = {
  className?: string;
  style?: CSSProp;
  disabledIds?: string[];
  selectedIds?: string[];
  entities: T[];
  onLoadMore?: (() => void) | null;
  multiple?: boolean;
  loading?: boolean;
  error?: boolean;
  setSelection: (selection: T[]) => void;
  onTryAgain: () => void;
  loadingMoreText?: React.ReactNode;
  errorText?: React.ReactNode;
  [key: string]: unknown;
};

const EntitiesTable = <T extends IdType>({
  className,
  style,
  disabledIds = [],
  selectedIds = [],
  entities = [],
  onLoadMore,
  multiple,
  loading,
  error,
  setSelection,
  onTryAgain,
  loadingMoreText,
  errorText,
  ...rest
}: EntitiesTableProps<T>) => {
  const selectedRowKeys = selectedIds;

  const tableRowProps = !multiple && {
    rowEventHandlers: {
      onClick: ({ rowData }: { rowData: T }) => {
        if (!disabledIds.includes(rowData.id)) {
          setSelection(selectedIds.includes(rowData.id) ? [] : [rowData]);
        }
      },
    },
    rowClassName: ({ rowData }: { rowData: T }) =>
      disabledIds.includes(rowData.id) ? 'TableRow--disabled' : 'TableRow',
  };
  const tableFooter = (
    <TableFooter
      loading={loading}
      error={error}
      onRetry={onTryAgain}
      loadingMoreText={loadingMoreText}
      errorText={errorText}
    />
  );
  return (
    <TableWrapper className={className} style={style as CSSProperties}>
      <AutoResizer>
        {({ width = 1000, height = 600 }) => (
          <BaseTable
            width={width}
            height={height}
            data={entities}
            selectedRowKeys={selectedRowKeys}
            footerRenderer={tableFooter}
            footerHeight={(loading || error) && entities.length > 0 ? 50 : 0}
            onEndReachedThreshold={300}
            onEndReached={() => {
              if (!onLoadMore || ((error || loading) && entities.length > 0)) {
                return;
              }
              onLoadMore();
            }}
            {...tableRowProps}
            {...rest}
          />
        )}
      </AutoResizer>
    </TableWrapper>
  );
};

const EntitiesTableWithSuspense = (props: EntitiesTableProps<IdType>) => {
  return (
    <AlloySuspense fallback={null}>
      <EntitiesTable {...props} />
    </AlloySuspense>
  );
};

export default Object.assign(EntitiesTableWithSuspense, {
  SelectionCell,
  SelectionCellHeader,
});
